// Voice UI: channel rows with live participants, the voice room view, the connection panel, incoming-call banner,
// and the "Voice & Video" settings tab.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { Channel, VoiceState, User } from "@majlis/protocol/types";
import { P, has } from "@majlis/protocol/permissions";
import { t, type Key } from "../lib/i18n.ts";
import { navigate } from "../lib/router.ts";
import { useStore, getState, voice, joinVoice, leaveVoice, declineCall, toast, type ServerState } from "../lib/store.ts";
import { micTest, VIDEO_PROFILES, profileById, type VoiceView, type InputMode, type VideoStat } from "../lib/voice.ts";
import { patch } from "../lib/api.ts";
import { Avatar, Icon, displayName, Field } from "./common.tsx";
import { toggleMediaFullscreen, mediaFullscreenActive, clearMediaExpansion, MEDIA_FULLSCREEN_CHANGE } from "../lib/media-fullscreen.ts";

export function useVoice(): VoiceView { return useSyncExternalStore(voice.subscribe, voice.getView); }
const ERR: Record<string, Key> = { mic_denied: "micDenied", no_mic: "noMic", channel_full: "channelFull", forbidden: "voiceForbidden", disconnected: "voiceDisconnected", mic_lost: "micLost", output_unavailable: "outputUnavailable" };

/** Surfaces engine errors as toasts once. */
export function useVoiceErrors() {
  const v = useVoice(); const last = useRef<string | null>(null);
  useEffect(() => { if (v.error && v.error !== last.current) toast(ERR[v.error] ? t(ERR[v.error]!) : v.error); last.current = v.error; }, [v.error]);
}

function userOf(id: string): User {
  const st = getState();
  if (st.me?.id === id) return st.me;
  return st.users[id] ?? Object.values(st.servers).map((s) => s.members?.[id]?.user).find(Boolean) ?? { id, username: "…", displayName: null, bio: null, avatarUrl: null };
}
function StateIcons({ s }: { s: VoiceState }) {
  return <span className="vicons">
    {(s.serverMute || s.suppress) && <span title={t(s.suppress ? "suppressed" : "serverMuted")}><Icon name="micOff" size={14} className="red" /></span>}
    {!s.serverMute && !s.suppress && s.selfMute && <span title={t("muted")}><Icon name="micOff" size={14} /></span>}
    {(s.selfDeaf || s.serverDeaf) && <span title={t("deafened")}><Icon name="headphonesOff" size={14} className={s.serverDeaf ? "red" : ""} /></span>}
    {s.selfVideo && <Icon name="video" size={14} />}{s.selfStream && <span className="live">{t("live")}</span>}
  </span>;
}

// ---------- sidebar row ----------
export function VoiceChannelRow({ server, channel, active, onSettings }: { server: ServerState; channel: Channel; active: boolean; onSettings?: () => void }) {
  const room = useStore((s) => s.voiceStates[channel.id]);
  const v = useVoice();
  const people = useMemo(() => Object.values(room ?? {}).sort((a, b) => a.joinedAt.localeCompare(b.joinedAt)), [room]);
  const canConnect = has(BigInt(channel.permissions), P.CONNECT);
  const limit = channel.userLimit || 0;
  const open = () => { navigate(`/channels/${server.id}/${channel.id}`); if (canConnect && v.channelId !== channel.id) joinVoice(channel.id); };
  return (
    <div className="vrow">
      <div className="chrow">
        <a className={`ch voice${v.channelId === channel.id ? " in" : ""}`} href={`/channels/${server.id}/${channel.id}`} aria-current={active ? "page" : undefined}
          onClick={(e) => { e.preventDefault(); open(); }} aria-disabled={!canConnect}>
          <Icon name={canConnect ? "volume" : "lock"} size={18} /><span dir="auto">{channel.name}</span>
          {limit > 0 && <span className="cnt" dir="ltr">{people.length}/{limit}</span>}
        </a>
        {onSettings && <button className="ib xs gear" onClick={onSettings} aria-label={t("channelSettings")}><Icon name="settings" size={14} /></button>}
      </div>
      {people.length > 0 && <ul className="vpeople" aria-label={t("inVoice")}>
        {people.map((s) => { const u = userOf(s.userId); return (
          <li key={s.userId} className={v.speaking[s.userId] ? "speaking" : ""}><Avatar user={u} size={22} /><span dir="auto">{displayName(u)}</span><StateIcons s={s} /></li>); })}
      </ul>}
    </div>
  );
}

// ---------- the room ----------
export function VoiceRoom({ title, channelId, serverId, canConnect, onMenu }: { title: string; channelId: string; serverId: string | null; canConnect: boolean; onMenu: () => void }) {
  const room = useStore((s) => s.voiceStates[channelId]);
  const me = useStore((s) => s.me)!;
  const v = useVoice();
  const people = useMemo(() => Object.values(room ?? {}).sort((a, b) => a.joinedAt.localeCompare(b.joinedAt)), [room]);
  const inHere = v.channelId === channelId && v.status !== "idle";
  const [menu, setMenu] = useState<string | null>(null);
  const canModerate = serverId ? (getState().servers[serverId] && has(BigInt(getState().servers[serverId]!.permissions), P.MUTE_MEMBERS)) : false;
  const shares = !inHere ? [] : [
    ...(v.screen ? [{ userId: me.id, stream: v.screen.stream, stat: null as VideoStat | null }] : []),
    ...Object.entries(v.remote).filter(([, r]) => r.screen).map(([uid, r]) => ({ userId: uid, stream: r.screen!, stat: v.peers[uid]?.inScreen ?? null })),
  ];
  return (
    <div className="vroom">
      <header className="top">
        <button className="ib menu-btn" onClick={onMenu} aria-label={t("openMenu")}><Icon name="menu" /></button>
        <div className="ttl"><Icon name={serverId ? "volume" : "phone"} size={20} /><b dir="auto">{title}</b>
          {inHere && <small><ConnBadge v={v} /></small>}</div>
      </header>
      <div className={`tiles n${Math.min(people.length + shares.length, 9)}${shares.length ? " has-share" : ""}`}>
        {shares.map((sh) => <ScreenTile key={`screen-${sh.userId}`} userId={sh.userId} stream={sh.stream} self={sh.userId === me.id} stat={sh.stat} />)}
        {people.map((s) => { const u = userOf(s.userId); const self = s.userId === me.id;
          const cam = inHere ? (self ? v.cam?.stream ?? null : v.remote[s.userId]?.cam ?? null) : null;
          return (
          <div key={s.userId} className={`tile${v.speaking[s.userId] && inHere ? " speaking" : ""}${cam ? " video" : ""}`} data-user={s.userId}>
            {cam ? <VideoEl stream={cam} mirror={self} className="cam" label={displayName(u)} /> : <Avatar user={u} size={88} animate={v.speaking[s.userId]} />}
            {cam && <div className="sctl"><MediaFullscreenButton /></div>}
            <div className="tname"><b dir="auto">{displayName(u)}</b><StateIcons s={s} /></div>
            {!self && inHere && <button className="ib xs tmenu" onClick={() => setMenu(menu === s.userId ? null : s.userId)} aria-label={t("userVolume")} aria-expanded={menu === s.userId}><Icon name="volume" size={16} /></button>}
            {menu === s.userId && <PeerMenu state={s} canModerate={!!canModerate} serverId={serverId} onClose={() => setMenu(null)} />}
            {!self && inHere && v.peers[s.userId] && <PeerQuality p={v.peers[s.userId]!} />}
          </div>); })}
        {!people.length && <p className="muted center">{t("voiceEmpty")}</p>}
      </div>
      <div className="vbar">
        {v.needsGesture && <button className="btn sm gold" onClick={() => voice.resumeAudio()}>{t("enableAudio")}</button>}
        {inHere ? <VoiceControls big /> : canConnect
          ? <button className="btn pri" id="voice-join" onClick={() => joinVoice(channelId)} disabled={v.status === "connecting"}><Icon name="phone" size={18} /> {t("joinVoice")}</button>
          : <p className="muted">{t("voiceForbidden")}</p>}
      </div>
    </div>
  );
}

function PeerQuality({ p }: { p: VoiceView["peers"][string] }) {
  const q = p.state !== "connected" ? "bad" : (p.rttMs ?? 0) > 250 || (p.lossPct ?? 0) > 5 ? "mid" : "good";
  return <span className={`pq ${q}`} title={`${t(p.state === "connected" ? "connected" : "connecting")} · ${p.rttMs ?? "–"} ms · ${p.lossPct ?? 0}% · ${p.codec ?? ""}`}><Icon name="signal" size={14} /></span>;
}

function PeerMenu({ state, canModerate, serverId, onClose }: { state: VoiceState; canModerate: boolean; serverId: string | null; onClose: () => void }) {
  const [vol, setVol] = useState(() => voice.settings.userVolumes[state.userId] ?? 1);
  const [muted, setMuted] = useState(() => !!voice.settings.localMutes[state.userId]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", h); document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [onClose]);
  const mod = (body: Record<string, unknown>) => patch(`/servers/${serverId}/members/${state.userId}/voice`, body).catch((e) => toast(String(e.message)));
  return (
    <div className="pmenu" ref={ref} role="dialog" aria-label={t("userVolume")}>
      <label><span>{t("userVolume")} <b dir="ltr">{Math.round(vol * 100)}%</b></span>
        <input type="range" min={0} max={2} step={0.05} value={vol} autoFocus onChange={(e) => { const n = Number(e.target.value); setVol(n); voice.saveSettings({ userVolumes: { ...voice.settings.userVolumes, [state.userId]: n } }); }} /></label>
      <label className="check"><input type="checkbox" checked={muted} onChange={(e) => { setMuted(e.target.checked); voice.saveSettings({ localMutes: { ...voice.settings.localMutes, [state.userId]: e.target.checked } }); }} /> {t("muteForMe")}</label>
      {canModerate && serverId && <div className="row wrap">
        <button className="btn sm ghost" onClick={() => mod({ mute: !state.serverMute })}>{t(state.serverMute ? "serverUnmute" : "serverMute")}</button>
        <button className="btn sm ghost" onClick={() => mod({ deaf: !state.serverDeaf })}>{t(state.serverDeaf ? "serverUndeafen" : "serverDeafen")}</button>
        <button className="btn sm danger" onClick={() => mod({ channelId: null })}>{t("disconnect")}</button>
      </div>}
    </div>
  );
}

function ConnBadge({ v }: { v: VoiceView }) {
  const label = v.status === "connected" ? t("voiceConnected") : v.status === "reconnecting" ? t("reconnecting") : t("connecting");
  return <span className={`conn-badge ${v.status}`} role="status">{label}{v.status === "connected" && v.pingMs !== null && <span dir="ltr"> · {v.pingMs} ms</span>}</span>;
}

/** Mute / deafen (/ leave) buttons. Used in the user bar, the room and the voice panel. */
export function VoiceControls({ big, withLeave = true }: { big?: boolean; withLeave?: boolean }) {
  const v = useVoice();
  const inVoice = v.status !== "idle";
  const ptt = voice.settings.inputMode === "ptt";
  return (
    <div className={`vctl${big ? " big" : ""}`}>
      <button className={`ib${v.selfMute ? " on-red" : ""}${v.transmitting ? " tx" : ""}`} aria-pressed={v.selfMute} onClick={() => voice.setMute(!v.selfMute)}
        aria-label={v.selfMute ? t("unmute") : t("mute")} title={`${v.selfMute ? t("unmute") : t("mute")} (Ctrl+Shift+M)`} id="voice-mute"><Icon name={v.selfMute ? "micOff" : "mic"} size={big ? 22 : 18} /></button>
      <button className={`ib${v.selfDeaf ? " on-red" : ""}`} aria-pressed={v.selfDeaf} onClick={() => voice.setDeaf(!v.selfDeaf)}
        aria-label={v.selfDeaf ? t("undeafen") : t("deafen")} title={`${v.selfDeaf ? t("undeafen") : t("deafen")} (Ctrl+Shift+D)`} id="voice-deafen"><Icon name={v.selfDeaf ? "headphonesOff" : "headphones"} size={big ? 22 : 18} /></button>
      {big && inVoice && <button className={`ib${v.cam ? " on-green" : ""}`} id="voice-cam" aria-pressed={!!v.cam} onClick={() => (v.cam ? voice.stopCamera() : voice.startCamera().catch((e) => toast(mediaErr(e))))}
        aria-label={v.cam ? t("cameraOff") : t("cameraOn")} title={v.cam ? t("cameraOff") : t("cameraOn")}><Icon name={v.cam ? "video" : "videoOff"} size={22} /></button>}
      {big && inVoice && <ShareButton />}
      {big && inVoice && ptt && <span className={`ptt${v.pttDown ? " down" : ""}`}>{t("pttHint", { key: keyLabel(voice.settings.pttKey) })}</span>}
      {withLeave && inVoice && <button className="ib leave" onClick={leaveVoice} aria-label={t("leaveVoice")} title={t("leaveVoice")} id="voice-leave"><Icon name="phoneOff" size={big ? 22 : 18} /></button>}
    </div>
  );
}

/** "Voice connected" panel above the user bar, plus global shortcuts. */
export function VoicePanel() {
  const v = useVoice();
  useVoiceErrors();
  const st = useStore((s) => s);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || !e.shiftKey) return;
      if (e.code === "KeyM") { e.preventDefault(); voice.setMute(!voice.view.selfMute); }
      else if (e.code === "KeyD") { e.preventDefault(); voice.setDeaf(!voice.view.selfDeaf); }
    };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, []);
  if (v.status === "idle" || !v.channelId) return null;
  const ch = v.serverId ? st.servers[v.serverId]?.channels.find((c) => c.id === v.channelId) : null;
  const dm = !v.serverId ? st.dms[v.channelId] : null;
  const name = ch ? ch.name : dm ? (dm.name ?? dm.recipients.map((r) => displayName(r)).join("، ")) : "";
  const where = ch && v.serverId ? `${ch.name} / ${st.servers[v.serverId]?.name ?? ""}` : name;
  return (
    <div className="vpanel">
      <div className="vinfo">
        <ConnBadge v={v} />
        <a href={v.serverId ? `/channels/${v.serverId}/${v.channelId}` : `/channels/@me/${v.channelId}`} dir="auto"
          onClick={(e) => { e.preventDefault(); navigate(v.serverId ? `/channels/${v.serverId}/${v.channelId}` : `/channels/@me/${v.channelId}`); }}>{where}</a>
      </div>
      <button className="ib leave" onClick={leaveVoice} aria-label={t("leaveVoice")} title={t("leaveVoice")}><Icon name="phoneOff" size={18} /></button>
    </div>
  );
}

// ---------- calls ----------
/** Ringing banner for incoming DM / group calls, with a synthesized ring tone. */
export function IncomingCalls() {
  const calls = useStore((s) => s.calls);
  const me = useStore((s) => s.me);
  const dms = useStore((s) => s.dms);
  const v = useVoice();
  const ringing = Object.values(calls).filter((c) => me && c.ringing.includes(me.id) && v.channelId !== c.channelId);
  useEffect(() => {
    if (!ringing.length) return;
    let ctx: AudioContext | null = null;
    try { ctx = new AudioContext(); } catch { return; }
    const ring = () => { if (!ctx || ctx.state !== "running") return; const now = ctx.currentTime;
      for (const [f, at] of [[880, 0], [660, 0.18], [880, 0.6], [660, 0.78]] as const) {
        const o = ctx.createOscillator(), g = ctx.createGain(); o.frequency.value = f; g.gain.setValueAtTime(0.0001, now + at);
        g.gain.exponentialRampToValueAtTime(0.12, now + at + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.16);
        o.connect(g); g.connect(ctx.destination); o.start(now + at); o.stop(now + at + 0.17); } };
    void ctx.resume().then(ring); const id = window.setInterval(ring, 2500);
    return () => { clearInterval(id); void ctx?.close(); };
  }, [ringing.map((c) => c.channelId).join()]);
  if (!ringing.length) return null;
  return <div className="calls" role="alertdialog" aria-live="assertive">{ringing.map((c) => {
    const dm = dms[c.channelId]; const caller = userOf(c.startedBy);
    return (
      <div key={c.channelId} className="call-card">
        <Avatar user={caller} size={56} animate />
        <div><b dir="auto">{dm?.name ?? displayName(caller)}</b><small>{t("incomingCall")}</small></div>
        <button className="ib accept" id="call-accept" aria-label={t("acceptCall")} title={t("acceptCall")} onClick={() => { navigate(`/channels/@me/${c.channelId}`); joinVoice(c.channelId); }}><Icon name="phone" /></button>
        <button className="ib leave" id="call-decline" aria-label={t("declineCall")} title={t("declineCall")} onClick={() => declineCall(c.channelId)}><Icon name="phoneOff" /></button>
      </div>);
  })}</div>;
}

/** Call button + in-call strip for a DM header. */
export function DmCallButton({ channelId, canCall }: { channelId: string; canCall: boolean }) {
  const v = useVoice();
  if (!canCall) return null;
  const inCall = v.channelId === channelId && v.status !== "idle";
  return <button className={`ib${inCall ? " on-green" : ""}`} id="dm-call" onClick={() => (inCall ? leaveVoice() : joinVoice(channelId))}
    aria-label={inCall ? t("leaveVoice") : t("startCall")} title={inCall ? t("leaveVoice") : t("startCall")}><Icon name={inCall ? "phoneOff" : "phone"} /></button>;
}
export function DmCallStrip({ channelId, title }: { channelId: string; title: string }) {
  const room = useStore((s) => s.voiceStates[channelId]);
  const v = useVoice();
  if (!room || !Object.keys(room).length) return null;
  return <div className="dm-call"><VoiceRoomCompact channelId={channelId} title={title} joined={v.channelId === channelId && v.status !== "idle"} /></div>;
}
function VoiceRoomCompact({ channelId, title, joined }: { channelId: string; title: string; joined: boolean }) {
  const room = useStore((s) => s.voiceStates[channelId]); const v = useVoice();
  const people = Object.values(room ?? {});
  return <div className="vcompact">
    <div className={`tiles small${v.screen || Object.values(v.remote).some((r) => r.screen) ? " has-share" : ""}`}>
      {joined && v.screen && <ScreenTile userId={getState().me!.id} stream={v.screen.stream} self stat={null} />}
      {joined && Object.entries(v.remote).filter(([, r]) => r.screen).map(([uid, r]) => <ScreenTile key={uid} userId={uid} stream={r.screen!} self={false} stat={v.peers[uid]?.inScreen ?? null} />)}
      {people.map((s) => { const u = userOf(s.userId); const self = s.userId === getState().me?.id;
        const cam = joined ? (self ? v.cam?.stream ?? null : v.remote[s.userId]?.cam ?? null) : null;
        return <div key={s.userId} className={`tile${v.speaking[s.userId] && joined ? " speaking" : ""}${cam ? " video" : ""}`} data-user={s.userId}>
          {cam ? <VideoEl stream={cam} mirror={self} className="cam" label={displayName(u)} /> : <Avatar user={u} size={56} />}{cam && <div className="sctl"><MediaFullscreenButton /></div>}<StateIcons s={s} /></div>; })}</div>
    {joined ? <VoiceControls big /> : <button className="btn pri" id="call-join" onClick={() => joinVoice(channelId)}><Icon name="phone" size={18} /> {t("joinCall")} <span className="sr-only">{title}</span></button>}
  </div>;
}

// ---------- settings ----------
const keyLabel = (code: string) => code.replace(/^Key/, "").replace(/^Digit/, "").replace("Backquote", "`");

export function VoiceSettingsTab() {
  const v = useVoice();
  const [s, setS] = useState(voice.settings);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [level, setLevel] = useState(-100);
  const [testing, setTesting] = useState(false);
  const [capture, setCapture] = useState(false);
  const stopRef = useRef<null | (() => void)>(null);
  const save = (p: Partial<typeof s>) => { voice.saveSettings(p); setS({ ...voice.settings }); };
  const refresh = () => navigator.mediaDevices?.enumerateDevices().then(setDevices).catch(() => {});
  useEffect(() => { void refresh(); navigator.mediaDevices?.addEventListener?.("devicechange", refresh); return () => { navigator.mediaDevices?.removeEventListener?.("devicechange", refresh); stopRef.current?.(); }; }, []);
  useEffect(() => {
    if (!capture) return;
    const h = (e: KeyboardEvent) => { e.preventDefault(); e.stopPropagation(); if (e.code !== "Escape") save({ pttKey: e.code }); setCapture(false); };
    window.addEventListener("keydown", h, true); return () => window.removeEventListener("keydown", h, true);
  }, [capture]);
  const inputs = devices.filter((d) => d.kind === "audioinput"), outputs = devices.filter((d) => d.kind === "audiooutput");
  const sinkSupported = typeof (AudioContext.prototype as any).setSinkId === "function";
  const shownLevel = v.status !== "idle" ? v.micLevel : level;
  const pct = (db: number) => Math.max(0, Math.min(100, ((db + 100) / 100) * 100));
  const toggleTest = async () => {
    if (testing) { stopRef.current?.(); stopRef.current = null; setTesting(false); setLevel(-100); return; }
    try {
      stopRef.current = await micTest({ deviceId: s.inputId !== "default" ? { exact: s.inputId } : undefined, echoCancellation: s.echoCancellation, noiseSuppression: s.noiseSuppression, autoGainControl: s.autoGainControl }, setLevel);
      setTesting(true); void refresh(); // labels appear once permission is granted
    } catch { toast(t("micDenied")); }
  };
  const label = (d: MediaDeviceInfo, i: number) => d.label || `${d.kind === "audioinput" ? t("microphone") : t("speaker")} ${i + 1}`;
  return (
    <div className="stack">
      <div className="grid2">
        <Field label={t("inputDevice")}><select id="vs-input" value={s.inputId} onChange={(e) => save({ inputId: e.target.value })}>
          <option value="default">{t("systemDefault")}</option>{inputs.filter((d) => d.deviceId && d.deviceId !== "default").map((d, i) => <option key={d.deviceId} value={d.deviceId}>{label(d, i)}</option>)}</select></Field>
        <Field label={t("outputDevice")} hint={sinkSupported ? undefined : t("outputUnsupported")}><select id="vs-output" value={s.outputId} disabled={!sinkSupported} onChange={(e) => save({ outputId: e.target.value })}>
          <option value="default">{t("systemDefault")}</option>{outputs.filter((d) => d.deviceId && d.deviceId !== "default").map((d, i) => <option key={d.deviceId} value={d.deviceId}>{label(d, i)}</option>)}</select></Field>
      </div>
      <Field label={`${t("inputVolume")} · ${Math.round(s.inputGain * 100)}%`}><input type="range" id="vs-gain" min={0} max={2} step={0.05} value={s.inputGain} onChange={(e) => save({ inputGain: Number(e.target.value) })} /></Field>
      <div className="meter-row">
        <button className="btn sm ghost" onClick={toggleTest} disabled={v.status !== "idle"}>{testing ? t("stopTest") : t("micTest")}</button>
        <div className="meter" role="meter" aria-label={t("micLevel")} aria-valuemin={-100} aria-valuemax={0} aria-valuenow={Math.round(shownLevel)}>
          <div className="fill" style={{ width: `${pct(shownLevel)}%` }} /><div className="thr" style={{ insetInlineStart: `${pct(s.vadDb)}%` }} /></div>
      </div>
      <h4>{t("inputMode")}</h4>
      <div className="row wrap">
        {(["vad", "ptt"] as InputMode[]).map((m) => <label key={m} className="check"><input type="radio" name="vs-mode" checked={s.inputMode === m} onChange={() => save({ inputMode: m })} /> {t(m === "vad" ? "voiceActivity" : "pushToTalk")}</label>)}
      </div>
      {s.inputMode === "vad" ? <Field label={`${t("sensitivity")} · ${s.vadDb} dB`} hint={t("sensitivityHint")}><input type="range" id="vs-vad" min={-90} max={-10} step={1} value={s.vadDb} onChange={(e) => save({ vadDb: Number(e.target.value) })} /></Field>
        : <div className="grid2">
          <Field label={t("pttKey")} hint={t("pttBrowserNote")}><button className="btn ghost" id="vs-ptt-key" onClick={() => setCapture(true)}>{capture ? t("pressAKey") : keyLabel(s.pttKey)}</button></Field>
          <Field label={`${t("pttRelease")} · ${s.pttReleaseMs} ms`}><input type="range" min={0} max={1000} step={20} value={s.pttReleaseMs} onChange={(e) => save({ pttReleaseMs: Number(e.target.value) })} /></Field>
        </div>}
      <h4>{t("voiceProcessing")}</h4>
      {([["echoCancellation", "echoCancellation"], ["noiseSuppression", "noiseSuppression"], ["autoGainControl", "autoGainControl"]] as const).map(([k, key]) => (
        <label key={k} className="toggle"><span><b>{t(key)}</b></span><input type="checkbox" role="switch" checked={s[k]} onChange={(e) => save({ [k]: e.target.checked })} /></label>))}
      <CameraSettings />
      <p className="muted small">{t("voiceTech")}</p>
    </div>
  );
}

const mediaErr = (e: unknown) => { const n = (e as DOMException)?.name; return n === "NotAllowedError" ? t("shareDenied") : n === "NotFoundError" ? t("noCamera") : String((e as Error)?.message ?? e); };

/** <video> bound to a MediaStream. Always muted: audio is routed through WebAudio for per-user volume. */
function MediaFullscreenButton() {
  const ref = useRef<HTMLButtonElement>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const tile = ref.current?.closest(".tile") ?? null;
    const video = tile?.querySelector("video");
    const update = () => setActive(mediaFullscreenActive(tile));
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && mediaFullscreenActive(tile)) {
        event.preventDefault();
        event.stopPropagation();
        void toggleMediaFullscreen(tile).catch(() => toast(t("fullscreenUnsupported")));
      }
    };
    document.addEventListener("fullscreenchange", update);
    document.addEventListener(MEDIA_FULLSCREEN_CHANGE, update);
    video?.addEventListener("webkitbeginfullscreen", update);
    video?.addEventListener("webkitendfullscreen", update);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("fullscreenchange", update);
      document.removeEventListener(MEDIA_FULLSCREEN_CHANGE, update);
      video?.removeEventListener("webkitbeginfullscreen", update);
      video?.removeEventListener("webkitendfullscreen", update);
      document.removeEventListener("keydown", escape);
      clearMediaExpansion(tile);
    };
  }, []);
  const label = t(active ? "exitFullscreen" : "fullscreen");
  return <button ref={ref} className="ib xs" onClick={(e) => void toggleMediaFullscreen(e.currentTarget.closest(".tile")).catch(() => toast(t("fullscreenUnsupported")))}
    aria-label={label} aria-pressed={active} title={label}><Icon name="expand" size={16} /></button>;
}

export function VideoEl({ stream, mirror, className = "", label, onEl }: { stream: MediaStream; mirror?: boolean; className?: string; label: string; onEl?: (el: HTMLVideoElement | null) => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => { const el = ref.current; if (!el) return; el.srcObject = stream; void el.play().catch(() => {}); onEl?.(el); return () => { onEl?.(null); }; }, [stream]);
  return <video ref={ref} className={`${className}${mirror ? " mirror" : ""}`} autoPlay playsInline muted aria-label={label}
    onDoubleClick={(e) => void toggleMediaFullscreen(e.currentTarget.closest(".tile") ?? e.currentTarget).catch(() => toast(t("fullscreenUnsupported")))} />;
}

function ScreenTile({ userId, stream, self, stat }: { userId: string; stream: MediaStream; self: boolean; stat: VideoStat | null }) {
  const u = userOf(userId); const v = useVoice();
  const [el, setEl] = useState<HTMLVideoElement | null>(null);
  const [vol, setVol] = useState(() => voice.settings.streamVolumes[userId] ?? 1);
  const hasAudio = stream.getAudioTracks().length > 0;
  const pip = async () => { try { if (document.pictureInPictureElement === el) await document.exitPictureInPicture(); else await el?.requestPictureInPicture(); } catch { toast(t("pipUnsupported")); } };
  const info = self ? v.screen?.actual : stat;
  return (
    <div className="tile screen" data-screen={userId}>
      <VideoEl stream={stream} className="share" label={t("screenOf", { name: displayName(u) })} onEl={setEl} />
      <div className="tname"><span className="live">{t("live")}</span><b dir="auto">{displayName(u)}</b>
        {info && info.width > 0 && <small dir="ltr">{info.width}×{info.height} · {info.fps} fps{stat?.codec ? ` · ${stat.codec.replace("video/", "")}` : ""}{stat?.hw ? " · HW" : ""}</small>}</div>
      <div className="sctl">
        {!self && hasAudio && <label className="svol" title={t("streamVolume")}><Icon name="volume" size={16} /><input type="range" min={0} max={2} step={0.05} value={vol} aria-label={t("streamVolume")}
          onChange={(e) => { const n = Number(e.target.value); setVol(n); voice.setStreamVolume(userId, n); }} /></label>}
        {"pictureInPictureEnabled" in document && <button className="ib xs" onClick={pip} aria-label={t("popOut")} title={t("popOut")}><Icon name="popout" size={16} /></button>}
        <MediaFullscreenButton />
        {self && <button className="btn sm danger" onClick={() => voice.stopScreen()}>{t("stopSharing")}</button>}
      </div>
    </div>
  );
}

function ShareButton() {
  const v = useVoice();
  const [open, setOpen] = useState(false);
  const [o, setO] = useState({ profile: voice.settings.shareProfile, audio: voice.settings.shareAudio, motion: voice.settings.shareMotion });
  const supported = !!navigator.mediaDevices?.getDisplayMedia;
  if (!supported) return null;
  if (v.screen) return <button className="ib on-green" id="voice-screen" aria-pressed onClick={() => voice.stopScreen()} aria-label={t("stopSharing")} title={t("stopSharing")}><Icon name="screen" size={22} /></button>;
  const go = () => { setOpen(false); voice.saveSettings({ shareProfile: o.profile, shareAudio: o.audio, shareMotion: o.motion }); voice.startScreen(o).catch((e) => { if ((e as DOMException)?.name !== "AbortError") toast(mediaErr(e)); }); };
  return <span className="share-wrap">
    <button className="ib" id="voice-screen" aria-expanded={open} onClick={() => setOpen(!open)} aria-label={t("shareScreen")} title={t("shareScreen")}><Icon name="screen" size={22} /></button>
    {open && <div className="pmenu share-menu" role="dialog" aria-label={t("shareScreen")}>
      <Field label={t("quality")}><select id="share-quality" value={o.profile} onChange={(e) => setO({ ...o, profile: e.target.value })}>
        {VIDEO_PROFILES.map((p) => <option key={p.id} value={p.id}>{p.height}p · {p.fps} fps</option>)}</select></Field>
      <label className="check"><input type="checkbox" checked={o.motion} onChange={(e) => setO({ ...o, motion: e.target.checked })} /> <span><b>{t("gamingMode")}</b><small>{t("gamingModeHint")}</small></span></label>
      <label className="check"><input type="checkbox" checked={o.audio} onChange={(e) => setO({ ...o, audio: e.target.checked })} /> <span><b>{t("shareAudio")}</b><small>{t("shareAudioHint")}</small></span></label>
      <p className="muted small">{t("qualityNote")}</p>
      <button className="btn pri" id="share-go" onClick={go}>{t("shareScreen")}</button>
    </div>}
  </span>;
}

export function CameraSettings() {
  const [s, setS] = useState(voice.settings);
  const [devs, setDevs] = useState<MediaDeviceInfo[]>([]);
  const [preview, setPreview] = useState<MediaStream | null>(null);
  useEffect(() => { navigator.mediaDevices?.enumerateDevices().then(setDevs).catch(() => {}); }, [preview]);
  useEffect(() => () => preview?.getTracks().forEach((t) => t.stop()), [preview]);
  const save = (p: Partial<typeof s>) => { voice.saveSettings(p); setS({ ...voice.settings }); };
  const cams = devs.filter((d) => d.kind === "videoinput");
  const test = async () => {
    if (preview) { preview.getTracks().forEach((t) => t.stop()); setPreview(null); return; }
    const pr = profileById(s.cameraProfile);
    try { setPreview(await navigator.mediaDevices.getUserMedia({ video: { ...(s.cameraId !== "default" ? { deviceId: { exact: s.cameraId } } : {}), width: { ideal: pr.width }, height: { ideal: pr.height }, frameRate: { ideal: pr.fps } } })); }
    catch (e) { toast(mediaErr(e)); }
  };
  const st = preview?.getVideoTracks()[0]?.getSettings();
  return <>
    <h4>{t("video")}</h4>
    <div className="grid2">
      <Field label={t("camera")}><select id="vs-camera" value={s.cameraId} onChange={(e) => save({ cameraId: e.target.value })}>
        <option value="default">{t("systemDefault")}</option>{cams.filter((d) => d.deviceId).map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${t("camera")} ${i + 1}`}</option>)}</select></Field>
      <Field label={t("cameraQuality")}><select id="vs-cam-quality" value={s.cameraProfile} onChange={(e) => save({ cameraProfile: e.target.value })}>
        {VIDEO_PROFILES.slice(0, 5).map((p) => <option key={p.id} value={p.id}>{p.height}p · {p.fps} fps</option>)}</select></Field>
    </div>
    <div className="row"><button className="btn sm ghost" onClick={test}>{preview ? t("stopTest") : t("testCamera")}</button>
      {st && <small className="muted" dir="ltr">{st.width}×{st.height} · {Math.round(st.frameRate ?? 0)} fps</small>}</div>
    {preview && <div className="cam-preview"><VideoEl stream={preview} mirror label={t("camera")} /></div>}
    <p className="muted small">{t("qualityNote")}</p>
  </>;
}
